import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Avatar, Skeleton } from "@chakra-ui/react";
import { FiHeart } from "react-icons/fi";
import { formatCount } from "./api";

const GAP = 16;
const MIN_COL = 220;
// Cover crops vary per character so the wall gets a natural, uneven skyline.
const RATIOS = [2 / 3, 3 / 4, 1, 4 / 5];
const TEXT_HEIGHT = 0.5; // rough height of the caption block, in card widths

export function coverRatio(publicId) {
  return RATIOS[parseInt(publicId.slice(-4), 16) % RATIOS.length] || RATIOS[0];
}

function WallCard({ character: c }) {
  const location = useLocation();
  const [broken, setBroken] = useState(false);
  const description = c.description?.trim();
  return (
    <article className="wall-card">
      <Link
        to={`/character/${c.publicId}`}
        state={{ from: location.pathname + location.search }}
        aria-label={`View ${c.name}`}
      >
        <div
          className="wall-cover"
          style={{ aspectRatio: String(coverRatio(c.publicId)) }}
        >
          {!broken && (
            <img
              src={c.card}
              alt=""
              loading="lazy"
              onError={() => setBroken(true)}
            />
          )}
        </div>
        <div className="wall-body">
          <div className="wall-char">
            <Avatar src={c.thumb} name={c.name} size="2xs" />
            <strong>{c.name}</strong>
          </div>
          {description && <p className="wall-desc">{description}</p>}
          <div className="wall-foot">
            <span className="wall-author">@{c.author || "creator"}</span>
            <span className="wall-likes">
              <FiHeart aria-hidden="true" />
              {formatCount(c.stats.likes)}
              <span className="sr-only"> likes</span>
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}

/** Waterfall layout: each card goes to the currently shortest column. */
export default function CharacterWall({ items, loading }) {
  const box = useRef(null);
  const [cols, setCols] = useState(4);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => {
      const n = Math.floor((el.getBoundingClientRect().width + GAP) / (MIN_COL + GAP));
      setCols(Math.max(2, Math.min(6, n)));
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const columns = useMemo(() => {
    const out = Array.from({ length: cols }, () => []);
    const heights = Array(cols).fill(0);
    items.forEach((c) => {
      let target = 0;
      for (let i = 1; i < cols; i++) if (heights[i] < heights[target]) target = i;
      out[target].push(c);
      heights[target] += 1 / coverRatio(c.publicId) + TEXT_HEIGHT;
    });
    return out;
  }, [items, cols]);

  return (
    <div ref={box} className="wall" style={{ "--cols": cols }}>
      {columns.map((col, i) => (
        <div className="wall-column" key={i}>
          {col.map((c) => (
            <WallCard key={c.publicId} character={c} />
          ))}
          {loading && !items.length && (
            <>
              {[1.5, 1.2, 1.35].map((h, k) => (
                <Skeleton
                  key={k}
                  className="wall-skeleton"
                  style={{ aspectRatio: String(1 / (h + ((i + k) % 3) * 0.12)) }}
                  startColor="subtle"
                  endColor="line"
                />
              ))}
            </>
          )}
        </div>
      ))}
    </div>
  );
}
